import { html, unsafeCSS } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_PATCH_MANAGEMENT_GENERAL } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  PatchManagementFormValue,
  PatchManagementSearchableKeywords,
} from '../../../../utils/uiSettings/patchManagement/patch-management.types';
import {
  getPatchManagementSearchableKeywords,
  PATCH_MANAGEMENT_DEFAULT_VALUE,
} from '../../../../utils/uiSettings/patchManagement/patch-management.values';
import { store } from '@nirvana/redux/store';
import {
  getIsPatchManagementAdditionalInfoLoaded,
  setIsLoadingForPatchSummaryWindow,
  setSelectedIdsForPatchSummaryWindow,
} from '../../../../redux/customStates/patchManagement/patch-management.actions';
import {
  MaintenanceWindowsApiClients,
  PolicyDependenciesContainer,
} from '../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { GzMaintenanceWindowsContainerWebComponent } from '../../helpers/maintenanceWindowsContainer';
import { MaintenanceWindowSelectOption, OnMaintenanceWindowOptionSelect } from '../../helpers/events/events';
import { CreateMaintenanceWindowDialog } from '../../helpers/dialogs/CreateMaintenanceWindowDialog';
import { RemoveMaintenanceWindowDialog } from '../../helpers/dialogs/RemoveMaintenanceWindowDialog';
import { DataLoadedStatus, PolicyAppState } from '../../../../redux/types';
import { selectPoliciesState } from '../../../../redux/slice';
import { PatchManagementMaintenanceWindow } from '@nirvana/shared/dtos/types/policies/common';

@customElement(WEB_COMPONENT_POLICY_PANEL_PATCH_MANAGEMENT_GENERAL)
export class GzPatchManagementGeneralWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  PatchManagementFormValue,
  PatchManagementSearchableKeywords,
  MaintenanceWindowsApiClients
> {
  /**
   * Path for Patch Management module.
   */
  protected path = {
    moduleId: ModuleId.PATCH_MANAGEMENT,
    sectionId: SectionId.NONE,
  };

  /**
   * Searchable keywords for Patch Management section.
   */
  protected searchableKeywords = getPatchManagementSearchableKeywords();

  @query('#maintenance-windows-container')
  private maintenanceWindowsContainerFormField: GzFormFieldWebComponent<
    GzMaintenanceWindowsContainerWebComponent,
    PatchManagementMaintenanceWindow[]
  >;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`
      <gz-title
        class="content"
        searchable-label="${this.searchableKeywords[PatchManagementSearchableKeywords.PatchManagement].label}"
        ><span>${this.searchableKeywords[PatchManagementSearchableKeywords.PatchManagement].translatedLabel}</span>
      </gz-title>
    `;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text id="patch-management-general-info-text" fontSize="12" fontWeight="400" class="infoText"
      >${translateFn(
        'Provides automatic patch assessment and installation through dedicated maintenance windows.'
      )}</gz-text
    >`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div id="patch-management-general-content">
        <gz-title
          searchable-label="${this.searchableKeywords[PatchManagementSearchableKeywords.PatchWindows].label}"
          class="module-subtitle"
          id="patch-management-general-maintenance-windows-title"
        >
          ${this.searchableKeywords[PatchManagementSearchableKeywords.PatchWindows].translatedLabel}
        </gz-title>

        <gz-form-field
          id="maintenance-windows-container"
          status-field
          @onMaintenanceWindowOptionSelect="${this.onMaintenanceWindowOptionSelected}"
          @onAddWindowClick="${this.onAddWindowClick}"
        >
          <gz-policy-panel-patch-management-maintenance-windows-container
            gz-form-input
          ></gz-policy-panel-patch-management-maintenance-windows-container>
        </gz-form-field>

        <div id="summary-container">
          <gz-policy-panel-patch-management-maintenance-window-summaries></gz-policy-panel-patch-management-maintenance-window-summaries>
        </div>
      </div>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.patchManagement
   */
  protected getValueFromManager(): PatchManagementFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.PATCH_MANAGEMENT]: PATCH_MANAGEMENT_DEFAULT_VALUE,
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.PATCH_MANAGEMENT]: PolicyPanelManager.getInstance().getPolicy('uiSettings.patchManagement'),
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Get additional information from manager and set missing fields.
   * Fields used in this section:
   * - integrityMonitor
   */
  protected getAdditionalInfoFromManager() {
    // get default value
    const defaultAdditionalInfo = {
      patchManagement: {
        availableMaintenanceWindows: [],
      },
    };

    // get value from manager
    const additionalInfo = {
      patchManagement: PolicyPanelManager.getInstance().getAdditionalPolicyInfo(['patchManagement']),
    };

    // merge received value with default one
    return deepmerge(defaultAdditionalInfo, additionalInfo);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        patchManagement: {
          maintenanceWindows: this.maintenanceWindowsContainerFormField,
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set readonly
    this.maintenanceWindowsContainerFormField.readonly = PolicyPanelManager.getInstance().getPolicy('readOnly', false);

    // set error state
    const policiesApp = selectPoliciesState(store.getState());
    this.maintenanceWindowsContainerFormField.Input.isError =
      getIsPatchManagementAdditionalInfoLoaded(policiesApp) === DataLoadedStatus.ERROR;

    // set available maintenance windows
    this.maintenanceWindowsContainerFormField.Input.availableMaintenanceWindows =
      this.additionalInfo.patchManagement.availableMaintenanceWindows;

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        patchManagement: {
          maintenanceWindows: this.valueT.uiSettings.patchManagement.maintenanceWindows,
        },
      },
    };
  }

  /**
   * Set clients.
   */
  protected setClients() {
    this.clients = PolicyDependenciesContainer.getInstance().resolveDependency(
      'MaintenanceWindows'
    ) as MaintenanceWindowsApiClients;
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'patchManagement'], {});
    }

    return value;
  }

  /**
   * On maintenance window option selected, show remove dialog or change summary details.
   * @param event
   */
  private async onMaintenanceWindowOptionSelected(event: OnMaintenanceWindowOptionSelect) {
    const { oldValue, newValue } = event.detail;

    // no option selected or no available maintenance windows
    if (!newValue) {
      return;
    }

    // no option selected on first load, do nothing
    if (!oldValue && !newValue.value) {
      return;
    }

    // no change
    if (oldValue && oldValue.value === newValue.value) {
      return;
    }

    // show remove dialog
    if (!newValue.value) {
      this.showRemoveMaintenanceWindowDialog(oldValue, newValue);
      return;
    }

    await this.changeSummaryDetails(newValue.value);
  }

  /**
   * On add window click, show add maintenance window dialog.
   */
  private onAddWindowClick() {
    this.showAddMaintenanceWindowDialog();
  }

  /**
   * Change summary details and add/remove loading state.
   * @param id
   */
  private async changeSummaryDetails(id: string) {
    // set loading state
    store.dispatch(setIsLoadingForPatchSummaryWindow(true));
    store.dispatch(setSelectedIdsForPatchSummaryWindow([id]));

    // check if maintenance window is cached
    if (!PolicyPanelManager.getInstance().isMaintenanceWindowCached(id)) {
      await this.getMaintenanceWindowById(id);
    }

    // remove loading state
    store.dispatch(setIsLoadingForPatchSummaryWindow(false));
  }

  /**
   * Show remove maintenance window dialog.
   * @param oldValue
   * @param newValue
   */
  private showRemoveMaintenanceWindowDialog(
    oldValue: MaintenanceWindowSelectOption,
    newValue: MaintenanceWindowSelectOption
  ) {
    const dialog = new RemoveMaintenanceWindowDialog(this.maintenanceWindowsContainerFormField, oldValue, newValue);
    dialog.show();
  }

  /**
   * Show add maintenance window dialog.
   */
  private showAddMaintenanceWindowDialog() {
    const dialog = new CreateMaintenanceWindowDialog(this.clients, this.maintenanceWindowsContainerFormField);
    dialog.show();
  }

  /**
   * Get maintenance window details by id and cache response.
   * @param id
   */
  private async getMaintenanceWindowById(id: string) {
    try {
      // get details from backend
      const response = await this.clients.GetMaintenanceWindowById(id);

      if (!response.data) {
        console.error('No maintenance window found');
        return;
      }

      // cache maintenance window details
      PolicyPanelManager.getInstance().cachePatchManagementSelectedMaintenanceWindowPolicyInfo(response.data);
    } catch (error) {
      // nothing to do, will be handled with a state error in GzMaintenanceWindowSummariesWebComponent
      console.error('No maintenance window found', error);
    }
  }

  /**
   * Override reset method, because we need to reset also maintenance window container.
   */
  public reset() {
    super.reset();
    this.maintenanceWindowsContainerFormField.Input.reset();
  }

  /**
   * Override method to check if additional info is loaded.
   * @param policiesApp
   */
  protected isAdditionalInfoLoaded(policiesApp: PolicyAppState): boolean {
    return getIsPatchManagementAdditionalInfoLoaded(policiesApp) !== DataLoadedStatus.NOT_LOADED;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_PATCH_MANAGEMENT_GENERAL: GzPatchManagementGeneralWebComponent;
  }
}
